import { useEffect, useState, type ReactNode } from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Brain,
  CheckCircle2,
  Network,
  Shield,
  Sparkles,
  Users,
} from 'lucide-react'
import { AppChrome } from '../components/AppChrome'
import { useAuth } from '../lib/auth'

type TeamRow = {
  team_id: string
  name: string
  status: string
  tier?: string
  template_id?: string | null
  member_count?: number
  adherence_score?: number
  compliance_status?: string
  super_team_eligible?: boolean
  skill_keys_bound?: string[]
}

type TemplateRow = {
  template_id: string
  name: string
  tier_start?: string
  charter?: string
  skill_keys?: string[]
  member_roles?: string[]
  peer_reviewers?: string[]
  outputs?: string[]
}

type Overview = {
  team_count: number
  super_teams: number
  teams: TeamRow[]
  thesis?: string
  default_templates?: {
    thesis?: string
    template_count?: number
    templates?: TemplateRow[]
    lifecycle?: string[]
    peer_graph?: Record<string, string[]>
  }
}

type EvolutionOverview = {
  quarantined: number
  promoted: number
  evolutions: {
    evolution_id: string
    title?: string
    skill_key?: string
    status?: string
    generation?: number
    confidence?: number
    body_md?: string
    body_sha256?: string
  }[]
  thesis?: string
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    ...init,
  })
  if (!res.ok) throw new Error(await res.text())
  return res.json() as Promise<T>
}

export function TeamsPage() {
  const { user, loading } = useAuth()
  const [overview, setOverview] = useState<Overview | null>(null)
  const [evolutions, setEvolutions] = useState<EvolutionOverview | null>(null)
  const [selected, setSelected] = useState<Record<string, unknown> | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [params] = useSearchParams()
  const linkedEvolution = params.get('evolution')
  const [linked, setLinked] = useState<EvolutionOverview['evolutions'][number] | null>(null)

  async function load() {
    setBusy(true)
    setError(null)
    try {
      const [teams, evo] = await Promise.all([
        api<Overview>('/api/afos360/v1/teams'),
        api<EvolutionOverview>('/api/afos360/v1/skill-evolutions'),
      ])
      setOverview(teams)
      setEvolutions(evo)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load teams')
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => {
    if (user) void load()
  }, [user])

  // An Approvals link opens that evolution directly, with its body and digest,
  // even when it is older than the newest evolutions listed below.
  useEffect(() => {
    if (!user || !linkedEvolution) {
      setLinked(null)
      return
    }
    let cancelled = false
    api<EvolutionOverview['evolutions'][number]>(
      `/api/afos360/v1/skill-evolutions/${encodeURIComponent(linkedEvolution)}`,
    )
      .then((ev) => {
        if (!cancelled) setLinked(ev)
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Evolution not found')
      })
    return () => {
      cancelled = true
    }
  }, [user, linkedEvolution, evolutions])

  if (!loading && !user) return <Navigate to="/signin" replace />

  async function bootstrap() {
    setBusy(true)
    setError(null)
    try {
      const result = await api<{ count: number }>('/api/afos360/v1/teams/bootstrap', {
        method: 'POST',
      })
      setMessage(
        `Bootstrapped ${result.count} tenant agent-skills teams from default TEAM templates.`,
      )
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bootstrap failed')
    } finally {
      setBusy(false)
    }
  }

  async function openTeam(teamId: string) {
    setBusy(true)
    try {
      const team = await api<Record<string, unknown>>(
        `/api/afos360/v1/teams/${encodeURIComponent(teamId)}`,
      )
      setSelected(team)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open team')
    } finally {
      setBusy(false)
    }
  }

  async function harvest() {
    setBusy(true)
    setError(null)
    try {
      const result = await api<{ harvested: number }>(
        '/api/afos360/v1/skill-evolutions/harvest',
        { method: 'POST' },
      )
      setMessage(`Harvested ${result.harvested} evolution proposal(s) into quarantine.`)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Harvest failed')
    } finally {
      setBusy(false)
    }
  }

  async function promote(evolutionId: string, bodySha256?: string) {
    if (!bodySha256) {
      setError('This evolution has no body digest to review; reload and retry.')
      return
    }
    // The static eval gate is a floor. Promotion binds the named reviewer to the
    // exact body digest shown on screen; the server refuses any other digest.
    if (!window.confirm(`Promote ${evolutionId}? You are approving the body shown (digest ${bodySha256.slice(0, 12)}…) as a named human reviewer.`)) {
      return
    }
    setBusy(true)
    setError(null)
    try {
      const binding = await api<Record<string, unknown>>(
        `/api/afos360/v1/skill-evolutions/${encodeURIComponent(evolutionId)}/promotion-binding`,
        { method: 'POST', body: JSON.stringify({ body_sha256: bodySha256 }) },
      )
      await api(`/api/afos360/v1/skill-evolutions/${encodeURIComponent(evolutionId)}/promote`, {
        method: 'POST',
        body: JSON.stringify({ confirm: 'PROMOTE_EVOLUTION', binding }),
      })
      setMessage(`Promoted ${evolutionId} into the skill library.`)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Promote failed')
    } finally {
      setBusy(false)
    }
  }

  async function peerQualify() {
    const teams = overview?.teams || []
    const subject =
      teams.find((t) => t.template_id === 'delivery_spine') || teams[0]
    const reviewer =
      teams.find((t) => t.template_id === 'quality_gate') ||
      teams.find((t) => t.team_id !== subject?.team_id) ||
      teams[1]
    if (!subject || !reviewer || subject.team_id === reviewer.team_id) {
      setError('Need Delivery Spine + Quality Gate (or any two teams) for peer qualification.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const qal = await api<{ qal_id: string; criteria: { id: string }[] }>(
        '/api/afos360/v1/teams/qualify',
        {
          method: 'POST',
          body: JSON.stringify({
            subject_team_id: subject.team_id,
            reviewer_team_id: reviewer.team_id,
            artifact_ref: 'workforce-mission-latest',
          }),
        },
      )
      const results = (qal.criteria || []).map((c) => ({
        id: c.id,
        passed: true,
        notes: 'Auto-pass for demo peer qual',
      }))
      await api(`/api/afos360/v1/teams/qualify/${encodeURIComponent(qal.qal_id)}/complete`, {
        method: 'POST',
        body: JSON.stringify({
          results,
          lessons_for_subject: ['Keep stage reports crisp for peer reviewers.'],
          lessons_for_reviewer: ['Score adherence after every peer qual.'],
        }),
      })
      setMessage(`Peer qualification ${qal.qal_id} completed — teams learned from each other.`)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Peer qualify failed')
    } finally {
      setBusy(false)
    }
  }

  const templates = overview?.default_templates?.templates || []
  const lifecycle = overview?.default_templates?.lifecycle || []

  return (
    <AppChrome>
      <main className="afos-page space-y-8">
        <header>
          <h1 className="afos-page-title">Agent teams</h1>
          <p className="afos-page-lead">
            Every tenant and project auto-provisions full agent skills and TEAM SDLC AI
            automation — first-in-class agile delivery from interview through evidence, with
            peer qualification and human merge gates.
          </p>
        </header>

        {(overview?.thesis || evolutions?.thesis) && (
          <div className="glass rounded-3xl p-5 text-sm text-[var(--color-mist)]">
            <p>{overview?.thesis}</p>
            {evolutions?.thesis && <p className="mt-2">{evolutions.thesis}</p>}
          </div>
        )}

        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat
            icon={<Users className="h-5 w-5 text-[var(--color-cyan)]" />}
            label="Teams"
            value={String(overview?.team_count ?? '—')}
          />
          <Stat
            icon={<Sparkles className="h-5 w-5 text-[var(--color-amber)]" />}
            label="Super-teams"
            value={String(overview?.super_teams ?? '—')}
          />
          <Stat
            icon={<Network className="h-5 w-5 text-[var(--color-cyan)]" />}
            label="Default templates"
            value={String(
              (overview?.default_templates?.template_count ?? templates.length) || '—',
            )}
          />
          <Stat
            icon={<Brain className="h-5 w-5 text-[var(--color-rose)]" />}
            label="Evolutions queued"
            value={String(evolutions?.quarantined ?? '—')}
          />
        </section>

        {lifecycle.length > 0 && (
          <section className="glass rounded-3xl p-5">
            <h2 className="font-[family-name:var(--font-display)] text-lg font-bold text-[var(--color-foam)]">
              Lifecycle coverage
            </h2>
            <p className="mt-1 text-sm text-[var(--color-mist)]">
              Best-in-class default pack every tenant starts from and extends.
            </p>
            <ol className="mt-3 flex flex-wrap gap-2 text-xs text-[var(--color-mist)]">
              {lifecycle.map((step, i) => (
                <li
                  key={step}
                  className="rounded-full border border-[var(--color-line)] bg-black/20 px-3 py-1"
                >
                  {i + 1}. {step}
                </li>
              ))}
            </ol>
          </section>
        )}

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => void bootstrap()}
            className="rounded-full bg-[var(--color-cyan)] px-4 py-2 text-sm font-semibold text-[var(--color-void)]"
          >
            Bootstrap default TEAM templates
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void peerQualify()}
            className="rounded-full border border-[var(--color-line)] px-4 py-2 text-sm text-[var(--color-foam)]"
          >
            Run peer qualification
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void harvest()}
            className="rounded-full border border-[var(--color-line)] px-4 py-2 text-sm text-[var(--color-foam)]"
          >
            Harvest skill evolutions
          </button>
        </div>

        {error && (
          <div className="rounded-2xl border border-[rgba(255,107,138,0.4)] bg-[rgba(255,107,138,0.1)] px-4 py-3 text-sm text-[var(--color-rose)]">
            {error}
          </div>
        )}
        {message && (
          <div className="rounded-2xl border border-[rgba(46,230,197,0.35)] bg-[rgba(46,230,197,0.08)] px-4 py-3 text-sm text-[var(--color-cyan)]">
            {message}
          </div>
        )}

        <section className="grid gap-6 lg:grid-cols-2">
          <div className="space-y-3">
            <h2 className="font-[family-name:var(--font-display)] text-2xl font-extrabold">
              Tenant teams
            </h2>
            {(overview?.teams || []).map((team, i) => (
              <motion.button
                key={team.team_id}
                type="button"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                onClick={() => void openTeam(team.team_id)}
                className="glass w-full rounded-3xl p-4 text-left"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-[family-name:var(--font-display)] text-lg font-bold text-[var(--color-foam)]">
                      {team.name}
                    </div>
                    <div className="mt-1 font-[family-name:var(--font-mono)] text-[10px] text-[var(--color-mist)]">
                      {team.team_id}
                      {team.template_id ? ` · tmpl:${team.template_id}` : ''}
                    </div>
                  </div>
                  <span className="rounded-full bg-white/5 px-3 py-1 text-[10px] uppercase tracking-wider text-[var(--color-cyan)]">
                    {team.tier || 'squad'}
                  </span>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-xs text-[var(--color-mist)]">
                  <span>{team.member_count ?? 0} members</span>
                  <span>adhere {(team.adherence_score ?? 0).toFixed?.(2) ?? team.adherence_score}</span>
                  <span>{team.compliance_status || 'pending'}</span>
                </div>
                {!!team.skill_keys_bound?.length && (
                  <div className="mt-2 text-[10px] text-[var(--color-mist)]">
                    skills: {team.skill_keys_bound.join(', ')}
                  </div>
                )}
              </motion.button>
            ))}
            {!overview?.teams?.length && (
              <p className="text-sm text-[var(--color-mist)]">
                No teams yet. Bootstrap the default TEAM template pack so this tenant can grow
                from Discovery through Release & Communications.
              </p>
            )}
          </div>

          <div className="space-y-4">
            <h2 className="font-[family-name:var(--font-display)] text-2xl font-extrabold">
              Default templates
            </h2>
            <p className="text-sm text-[var(--color-mist)]">
              Platform starting pack. Tenants instantiate, then extend skills and promote
              evolutions — never silent self-promote.
            </p>
            <ul className="space-y-2">
              {templates.map((tmpl) => (
                <li
                  key={tmpl.template_id}
                  className="rounded-2xl border border-[var(--color-line)] bg-black/25 px-4 py-3"
                >
                  <div className="font-semibold text-[var(--color-foam)]">{tmpl.name}</div>
                  <div className="mt-1 font-[family-name:var(--font-mono)] text-[10px] text-[var(--color-mist)]">
                    {tmpl.template_id} · {tmpl.tier_start || 'squad'}
                  </div>
                  <p className="mt-2 text-xs text-[var(--color-mist)] line-clamp-3">
                    {tmpl.charter}
                  </p>
                  {!!tmpl.skill_keys?.length && (
                    <div className="mt-2 text-[10px] text-[var(--color-cyan)]">
                      {(tmpl.skill_keys || []).join(' · ')}
                    </div>
                  )}
                </li>
              ))}
            </ul>

            <h2 className="pt-2 font-[family-name:var(--font-display)] text-2xl font-extrabold">
              Skill library evolution
            </h2>
            <ul className="space-y-2">
              {[
                ...(linked ? [linked] : []),
                ...(evolutions?.evolutions || [])
                  .filter((ev) => ev.evolution_id !== linked?.evolution_id)
                  .slice(0, 8),
              ].map((ev) => (
                <li
                  key={ev.evolution_id}
                  id={`evolution-${ev.evolution_id}`}
                  className={`rounded-2xl border bg-black/25 px-4 py-3 ${
                    ev.evolution_id === linked?.evolution_id
                      ? 'border-[var(--color-amber)]'
                      : 'border-[var(--color-line)]'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      {ev.evolution_id === linked?.evolution_id && (
                        <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-[var(--color-amber)]">
                          Opened from Approvals
                        </div>
                      )}
                      <div className="font-semibold text-[var(--color-foam)]">
                        {ev.title || ev.skill_key}
                      </div>
                      <div className="mt-1 text-xs text-[var(--color-mist)]">
                        {ev.skill_key} · gen {ev.generation} · {ev.status} · conf{' '}
                        {ev.confidence ?? '—'}
                        {ev.body_sha256 && <> · digest {ev.body_sha256.slice(0, 12)}…</>}
                      </div>
                      {ev.body_md && (
                        <details className="mt-2 text-xs text-[var(--color-mist)]">
                          <summary className="cursor-pointer">Review body before promoting</summary>
                          <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap rounded-xl bg-black/30 p-2">
                            {ev.body_md}
                          </pre>
                        </details>
                      )}
                    </div>
                    {ev.status === 'quarantined' && (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void promote(ev.evolution_id, ev.body_sha256)}
                        className="shrink-0 rounded-full border border-[var(--color-line)] px-3 py-1 text-xs"
                      >
                        Promote
                      </button>
                    )}
                  </div>
                </li>
              ))}
              {!evolutions?.evolutions?.length && (
                <p className="text-sm text-[var(--color-mist)]">
                  No evolutions yet. Run harvest after teams share learnings or learning ledger
                  quarantines exist.
                </p>
              )}
            </ul>

            {selected && (
              <div className="glass rounded-3xl p-5">
                <div className="flex items-center gap-2 text-[var(--color-cyan)]">
                  <Network className="h-4 w-4" />
                  <span className="text-xs font-semibold uppercase tracking-[0.16em]">
                    Team detail
                  </span>
                </div>
                <h3 className="mt-2 font-[family-name:var(--font-display)] text-xl font-bold">
                  {String(selected.name)}
                </h3>
                <p className="mt-2 text-sm text-[var(--color-mist)]">
                  {String(selected.charter || '')}
                </p>
                <div className="mt-3 flex items-center gap-2 text-xs text-[var(--color-mist)]">
                  <Shield className="h-3.5 w-3.5" />
                  tier={String(selected.tier)} · status={String(selected.status)}
                  {selected.template_id ? ` · tmpl=${String(selected.template_id)}` : ''}
                </div>
                <div className="mt-2 flex items-center gap-2 text-xs text-[var(--color-mist)]">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  skills={(
                    ((selected.building as { skill_keys_bound?: string[] } | undefined)
                      ?.skill_keys_bound || [])
                  ).join(', ') || '—'}
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
    </AppChrome>
  )
}

function Stat({
  icon,
  label,
  value,
}: {
  icon: ReactNode
  label: string
  value: string
}) {
  return (
    <div className="glass rounded-3xl p-5">
      <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-[var(--color-mist)]">
        {icon}
        {label}
      </div>
      <div className="font-[family-name:var(--font-display)] text-3xl font-extrabold text-[var(--color-foam)]">
        {value}
      </div>
    </div>
  )
}
